import { yupResolver } from "@hookform/resolvers/yup";
import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import {
	Button,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import { useRouter } from "next/router";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import type { Deal } from "~/models/Deal";
import type { DealRepairParam } from "~/models/DealRepair";
import { validationSchema } from "~/models/DealRepair";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { duplicateWithRepair } from "~/utils/api/deals/DealDuplication";
import { update } from "~/utils/api/deals/DealRepair";
import { onInvalidScroll } from "~/utils/FormUtil";
import { DealRepairCardModalForm } from "./DealRepairCardModalForm";

type Props = DeepReadonly<{
	deal: Deal;
	duplicate?: boolean;
	handleClose: () => void;
	handleMutate?: (() => Promise<void>) | null;
	isOpen: boolean;
}>;

export const DealRepairCardModal: React.FC<Props> = ({
	deal,
	isOpen,
	handleClose,
	handleMutate = null,
	duplicate = false
}: Props) => {
	const repair = deal.dealRepair;
	const router = useRouter();
	const description = duplicate
		? "同一物件で、分離発注をする場合はこちらに入力してください。"
		: "";
	const defaultValues = React.useMemo(
		(): DealRepairParam => ({
			checkDestroy: false,
			itemCategory: repair?.itemCategory ?? "",
			itemName: repair?.itemName ?? "",
			manufacturer: repair?.manufacturer ?? "",
			modelNumber: repair?.modelNumber ?? "",
			modelNumberFile: null,
			requestContent: repair?.requestContent ?? ""
		}),
		[repair]
	);
	const methods = useForm<DealRepairParam>({
		defaultValues,
		mode: "onBlur",
		resolver: yupResolver(validationSchema)
	});
	const handleModalSubmit = React.useCallback(() => {
		methods.handleSubmit(
			async (submitValue: DealRepairParam) => {
				const response = await update(deal.id, submitValue);
				if (isSuccess(response)) {
					await handleMutate?.();
					handleClose();
					toast.success("更新しました");
					return;
				}
				handleClose();
				toast.error("エラーが発生しました");
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [methods, deal.id, handleClose, handleMutate]);
	const handleSubmitWithRepairDuplicate = React.useCallback(() => {
		methods.handleSubmit(
			async (submitValue: DealRepairParam) => {
				const result = await duplicateWithRepair(submitValue, deal.id);
				if (isSuccess(result)) {
					const newId = result.data?.id ?? null;
					if (newId !== null) {
						await router.push(PATH(newId));
						toast.success("関連案件として追加しました");
						return;
					}
				}
				handleClose();
				toast.error("エラーが発生しました");
			},
			(errors) => {
				onInvalidScroll(errors);
			}
		)();
	}, [methods, deal.id, handleClose, router]);
	return (
		<Modalv2 isOpen={isOpen} onClose={handleClose} size="l">
			<ModalHeader description={description} title="修繕情報の更新" />
			<ModalBody>
				<FormProvider {...methods}>
					<DealRepairCardModalForm deal={deal} />
				</FormProvider>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button onClick={handleClose} variant="secondary">
						保存せず閉じる
					</Button>
				}
				rightArea={
					<Button
						disabled={methods.formState.isSubmitting}
						onClick={
							duplicate ? handleSubmitWithRepairDuplicate : handleModalSubmit
						}
						variant="primary"
					>
						{duplicate ? "追加" : "保存"}
					</Button>
				}
			/>
		</Modalv2>
	);
};
